/**
 * FailureBreakdown (TRA-835) — what kind of mistake the model made, from the
 * Mercor failure-classification hierarchy, as a Pareto of leaf failure
 * types: ranked bars on a shared absolute axis (a 51% bar is half the
 * track). Hovering a type explains it and names its family.
 *
 * One attribution row per run, so the breakdown re-slices honestly under the
 * page's model filter. The denominator is stated outright: runs whose failure
 * is attributed to the model itself. Non-model issues (verifier, prompt,
 * tool, context) and unknown-evidence runs are excluded from the shares but
 * reported, never silently dropped.
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type {
  AttributionBreakdown,
  AttributionFamilyShare,
} from "@/components/benchmarks/benchmarkAnalysis";

/**
 * Plain-language gloss per leaf failure type. UI copy explaining the
 * artifact's own categories — the taxonomy itself still travels as data,
 * and a leaf without an entry renders without a tooltip rather than
 * inventing one.
 */
const LEAF_NOTES: Record<string, string> = {
  reasoning:
    "The chain of logic went wrong: the model set up the wrong approach or drew the wrong conclusion from correct inputs.",
  instruction_following:
    "The model ignored or misread an explicit instruction in the prompt.",
  verification:
    "The model never checked its work, when a read-back or sanity check would have caught the error.",
  calculation: "The math itself went wrong: right plan, wrong numbers.",
  tool_execution:
    "The model misused a working tool, calling the wrong operation or passing bad arguments. A broken tool would count as a non-model issue instead.",
  answer_composition:
    "The work was largely right but the submitted answer was incomplete, mis-formatted, or missing required pieces.",
  context_management:
    "The model lost its own state or overflowed its context mid-task.",
  search: "The model never found the document or data it needed.",
  source_selection:
    "The model picked the wrong document, version, period, or controlling authority before doing the downstream work.",
  extraction: "The model pulled the wrong value out of the right source.",
  context_utilization:
    "The needed fact was right there in the prompt or retrieved context, but went unused.",
  domain_intuition:
    "The model lacked an expert heuristic or domain convention the task assumed. A genuine knowledge gap, confirmed by trace audit.",
  unresolved_model_mechanism:
    "A confirmed model failure whose mechanism could not be pinned down from the trace.",
  // The v3 dreaming-pipeline taxonomy collapses the hierarchy to three leaves.
  reasoning_issue:
    "The chain of logic went wrong: the model set up the wrong approach, drew the wrong conclusion, or never verified its work.",
  missing_domain_knowledge:
    "The model lacked an expert heuristic or domain convention the task assumed. A genuine knowledge gap, confirmed by trace audit.",
  execution_issue:
    "The plan was sound but the execution failed: wrong numbers, misused tools, or an incomplete final answer.",
};

/** Same treatment for the non-model issue types: gloss what each means. */
const NON_MODEL_NOTES: Record<string, string> = {
  bad_verifier_rubric:
    "The grader itself was wrong: the rubric or verifier rejected correct work or accepted a broken premise.",
  insufficient_budget:
    "The run hit a turn, token, or time cap before it could finish. More budget could have changed the outcome.",
  broken_tools:
    "A tool the task depends on errored or misbehaved. Misusing a working tool would count as a model failure instead.",
  ambiguous_prompt:
    "The task prompt underspecified what was wanted, so a reasonable reading still failed the rubric.",
  missing_context:
    "The task did not provide a document or fact the work required.",
};

function formatShare(share: number): string {
  return `${(share * 100).toFixed(1)}%`;
}

/** Header line: the denominator, or which of the three empty states holds. */
function subtitleText(b: AttributionBreakdown): string {
  let base: string;
  if (b.modelFailureCount > 0) {
    const noun = b.modelFailureCount === 1 ? "run" : "runs";
    base = `Share of the ${b.modelFailureCount.toString()} model-failure ${noun} in scope`;
  } else if (b.totalRunCount === 0) {
    base = "No failure classifications exported for this benchmark";
  } else if (b.runCount === 0) {
    base = "No classified runs for the selected model";
  } else {
    base = `No model failures among the ${b.runCount.toString()} classified runs in scope`;
  }
  const excluded: string[] = [];
  if (b.nonModelIssueCount > 0)
    excluded.push(
      `${b.nonModelIssueCount.toString()} non-model issues (shown below)`,
    );
  if (b.unknownCount > 0) excluded.push(`${b.unknownCount.toString()} unknown`);
  return excluded.length > 0
    ? `${base} · excludes ${excluded.join(", ")}`
    : base;
}

/** Body copy for the zero-model-failures states — each cause named explicitly. */
function emptyStateText(b: AttributionBreakdown): string {
  if (b.totalRunCount === 0) {
    return "No failure-classification pass has been exported for this benchmark yet.";
  }
  if (b.runCount === 0) {
    return `The classification pass covers ${b.totalRunCount.toString()} runs of other models; the selected model was not part of it.`;
  }
  if (b.nonModelIssueCount === 0 && b.unknownCount === 0) {
    return "No classified failures in scope: every classified run passed or was ungradable.";
  }
  if (b.nonModelIssueCount === 0) {
    return "Every classified failure in scope lacked enough evidence to attribute a cause.";
  }
  if (b.unknownCount === 0) {
    return "Every classified failure in scope was attributed to the task or environment, not the model.";
  }
  return "Every classified failure in scope was attributed to the task or environment, or lacked enough evidence to attribute a cause.";
}

interface ParetoRow {
  leaf: string;
  label: string;
  familyLabel: string;
  count: number;
  taskCount: number;
  share: number;
}

/** All leaves ranked across families, each keeping its family's name. */
function paretoRows(families: AttributionFamilyShare[]): ParetoRow[] {
  return families
    .flatMap((family) =>
      family.leaves.map((leaf) => ({ ...leaf, familyLabel: family.label })),
    )
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

function LeafLabel({
  row,
  dimmed,
}: {
  row: ParetoRow;
  dimmed: boolean;
}): React.JSX.Element {
  const note = LEAF_NOTES[row.leaf];
  const tone = dimmed ? "text-muted-foreground" : "text-foreground";
  if (note === undefined) {
    return <span className={`text-sm ${tone}`}>{row.label}</span>;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={`text-sm ${tone} underline decoration-border decoration-dotted underline-offset-4`}
        >
          {row.label}
        </span>
      </TooltipTrigger>
      {/* side=right + pointer-events-none: the default top placement covered
          the rows above and swallowed the cursor on the way up. */}
      <TooltipContent side="right" className="pointer-events-none max-w-80">
        <p>{note}</p>
        <p className="mt-1 text-muted-foreground">Family: {row.familyLabel}</p>
        <p className="mt-1 text-muted-foreground">
          Click to filter the task table.
        </p>
      </TooltipContent>
    </Tooltip>
  );
}

function LeafRow({
  row,
  selectedLeaf,
  onSelectLeaf,
}: {
  row: ParetoRow;
  selectedLeaf: string | null;
  onSelectLeaf: (leaf: string | null) => void;
}): React.JSX.Element {
  const selected = selectedLeaf === row.leaf;
  const dimmed = selectedLeaf !== null && !selected;
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={() => {
        onSelectLeaf(selected ? null : row.leaf);
      }}
      className="grid w-full grid-cols-[14rem_1fr_8rem] items-center gap-x-4 py-0.5 text-left hover:bg-muted/50"
    >
      <LeafLabel row={row} dimmed={dimmed} />
      <div className="h-1.5 bg-muted">
        <div
          className={`h-1.5 ${dimmed ? "bg-primary/25" : "bg-primary"}`}
          style={{ width: `${(row.share * 100).toString()}%` }}
        />
      </div>
      <span
        className={`whitespace-nowrap text-right font-mono text-sm tabular-nums ${
          dimmed ? "text-muted-foreground" : "text-foreground"
        }`}
      >
        {formatShare(row.share)}
        <span className="ml-2 text-xs text-muted-foreground">{row.count}</span>
      </span>
    </button>
  );
}

export function FailureBreakdown({
  breakdown,
  selectedLeaf,
  onSelectLeaf,
}: {
  breakdown: AttributionBreakdown;
  selectedLeaf: string | null;
  onSelectLeaf: (leaf: string | null) => void;
}): React.JSX.Element {
  const rows = paretoRows(breakdown.families);
  // The selection channel covers both sections: model-failure leaves and
  // non-model issue types share one id space.
  const selected =
    rows.find((row) => row.leaf === selectedLeaf) ??
    breakdown.nonModelIssues.find((row) => row.leaf === selectedLeaf);
  // A stale URL (?failure=X in a scope without X) has no row to click, so
  // the escape hatch must render explicitly or the ledger stays stranded.
  const staleSelection = selectedLeaf !== null && selected === undefined;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-foreground">
          Failure types
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {subtitleText(breakdown)}
          {selected !== undefined &&
            ` · task table filtered to ${selected.label}: ${selected.count.toString()} ${
              selected.count === 1 ? "run" : "runs"
            } across ${selected.taskCount.toString()} ${selected.taskCount === 1 ? "task" : "tasks"}`}
        </p>
      </CardHeader>
      <CardContent>
        {staleSelection && (
          <p className="mb-3 text-xs text-muted-foreground">
            The task table is filtered to a failure type with no runs in this
            scope.{" "}
            <button
              type="button"
              className="underline underline-offset-4"
              onClick={() => {
                onSelectLeaf(null);
              }}
            >
              Clear filter
            </button>
          </p>
        )}
        {breakdown.modelFailureCount === 0 ? (
          <p className="text-sm text-muted-foreground">
            {emptyStateText(breakdown)}
          </p>
        ) : (
          <div className="space-y-0.5">
            {rows.map((row) => (
              <LeafRow
                key={row.leaf}
                row={row}
                selectedLeaf={selectedLeaf}
                onSelectLeaf={onSelectLeaf}
              />
            ))}
          </div>
        )}
        {breakdown.nonModelIssues.length > 0 && (
          <div className="mt-4 border-t border-border pt-3">
            <p className="mb-1.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              non-model issues · {breakdown.nonModelIssueCount} runs where the
              task or environment failed
            </p>
            <div className="space-y-0.5">
              {breakdown.nonModelIssues.map((row) => {
                const rowSelected = selectedLeaf === row.leaf;
                const dimmed = selectedLeaf !== null && !rowSelected;
                const note = NON_MODEL_NOTES[row.leaf];
                return (
                  <button
                    key={row.leaf}
                    type="button"
                    aria-pressed={rowSelected}
                    onClick={() => {
                      onSelectLeaf(rowSelected ? null : row.leaf);
                    }}
                    className="grid w-full grid-cols-[14rem_1fr_8rem] items-center gap-x-4 py-0.5 text-left hover:bg-muted/50"
                  >
                    {note !== undefined ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-sm text-muted-foreground underline decoration-border decoration-dotted underline-offset-4">
                            {row.label}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent
                          side="right"
                          className="pointer-events-none max-w-80"
                        >
                          <p>{note}</p>
                          <p className="mt-1 text-muted-foreground">
                            {row.count} {row.count === 1 ? "run" : "runs"}{" "}
                            across {row.taskCount}{" "}
                            {row.taskCount === 1 ? "task" : "tasks"}
                          </p>
                          <p className="mt-1 text-muted-foreground">
                            Click to filter the task table.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <span className="text-sm text-muted-foreground">
                        {row.label}
                      </span>
                    )}
                    <div className="h-1.5 bg-muted">
                      <div
                        className={`h-1.5 ${dimmed ? "bg-muted-foreground/20" : "bg-muted-foreground/50"}`}
                        style={{ width: `${(row.share * 100).toString()}%` }}
                      />
                    </div>
                    <span className="whitespace-nowrap text-right font-mono text-sm tabular-nums text-muted-foreground">
                      {formatShare(row.share)}
                      <span className="ml-2 text-xs">{row.count}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
